'use client';

import React, { createContext, useContext, useState, ReactNode, DragEvent, ClipboardEvent } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { Message } from '@ai-sdk/react';
import { DEFAULT_MODEL_ID } from 'mode-shared/constants';

export interface UserImage {
  id: string;
  name: string;
  url: string;
  contentType: string;
  size: number;
}

interface TerminalContextProps {
  // images
  images: UserImage[];
  attachementsSize: number;
  error: string | null;
  allowedTypes: string[];
  maxSize: number;
  isDragging: boolean;
  setIsDragging: React.Dispatch<React.SetStateAction<boolean>>;
  fileType: string;
  addImage: (image: UserImage) => void;
  removeImage: (image: UserImage) => void;
  handleFileSelect: (file: File) => Promise<UserImage | undefined>;
  handleDragOver: (event: DragEvent) => void;
  handleDragLeave: (event: DragEvent) => void;
  handleDrop: (event: DragEvent) => void;
  handlePaste: (event: ClipboardEvent) => void;
  setError: (error: string | null) => void;
  setImages: React.Dispatch<React.SetStateAction<UserImage[]>>;
  setAttachementsSize: React.Dispatch<React.SetStateAction<number>>;
  // messages
  message?: Message;
  setMessage: React.Dispatch<React.SetStateAction<Message | undefined>>;
  // model selection
  selectedModel: string;
  handleModelChange: (modelId: string) => void;
}

const TerminalContext = createContext<TerminalContextProps | undefined>(undefined);

export const useTerminalContext = () => {
  const context = useContext(TerminalContext);
  if (!context) {
    throw new Error('useTerminalContext must be used within a TerminalContextProvider');
  }
  return context;
};

export const TerminalContextProvider = ({ children }: { children: ReactNode }) => {
  const [images, setImages] = useState<UserImage[]>([]);
  const [attachementsSize, setAttachementsSize] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const maxSize = 3 * 1024 * 1024; // 3 MB
  const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
  const fileType = allowedTypes.join(',');
  const [message, setMessage] = useState<Message | undefined>();
  const [selectedModel, setSelectedModel] = useState<string>(DEFAULT_MODEL_ID);

  const addImage = (image: UserImage) => {
    setAttachementsSize((prev) => prev + image.size);
    setImages((prev) => [...prev, image]);
  };

  const removeImage = (image: UserImage) => {
    setAttachementsSize((prev) => prev - image.size);
    setImages((prev) => prev.filter((i) => i.id !== image.id));
  };

  async function getBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
    });
  }

  const handleFileSelect = (file: File) => {
    setError(null);
    if (!allowedTypes.includes(file.type)) {
      setError('Please select a PNG, JPEG, or JPG image');
      return Promise.resolve(undefined);
    }
    if (attachementsSize + file.size > maxSize) {
      setError(`File size must be less than ${maxSize / (1024 * 1024)}MB`);
      return Promise.resolve(undefined);
    }
    return getBase64(file)
      .then((base64) => {
        const image = {
          id: uuidv4(),
          name: file.name,
          url: base64,
          contentType: file.type,
          size: file.size,
        };
        addImage(image);
        return image;
      })
      .catch((error) => {
        setError('Error converting file to base64');
        console.error('Error converting file to base64:', error);
        return undefined;
      });
  };

  const handleDragOver = (event: DragEvent) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event: DragEvent) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (event: DragEvent) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handlePaste = (event: ClipboardEvent) => {
    const items = event.clipboardData?.items;
    if (!items) return;
    Array.from(items).forEach((item) => {
      if (item.type.startsWith('image/')) {
        event.preventDefault();
        const file = item.getAsFile();
        if (file) {
          handleFileSelect(file);
        }
      }
    });
  };

  const handleModelChange = (modelId: string) => {
    setSelectedModel(modelId);
  };

  return (
    <TerminalContext.Provider
      value={{
        images,
        attachementsSize,
        error,
        allowedTypes,
        maxSize,
        isDragging,
        setIsDragging,
        fileType,
        addImage,
        removeImage,
        handleFileSelect,
        handleDragOver,
        handleDragLeave,
        handleDrop,
        handlePaste,
        setError,
        setImages,
        setAttachementsSize,
        message,
        setMessage,
        selectedModel,
        handleModelChange,
      }}
    >
      {children}
    </TerminalContext.Provider>
  );
};
